import {
  Company_Read_Nested,
  deleteV1CompaniesCompanyIdLogo,
  getV1Companies,
  getV1CompaniesCompanyIdConnectedCompanies,
  GetV1CompaniesCompanyIdConnectedCompaniesData,
  getV1CompaniesCompanyIdConnectedCompaniesTypeahead,
  GetV1CompaniesCompanyIdConnectedCompaniesTypeaheadData,
  getV1CompaniesCompanyIdLogo,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdConnectedCompanies,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdConnectedCompaniesTypeahead,
  GetV1CompaniesData,
  getV1CompaniesId,
  getV1CompaniesTypeahead,
  GetV1CompaniesTypeaheadData,
  patchV1CompaniesId,
  postV1CompaniesCompanyIdLogo,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { string } from 'yup';

import { DEFAULT_API_PAGE_SIZE, MAX_API_PAGE_SIZE } from '~constants/consts';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import { addressIsGeocoded } from '~hooks/useAddress/models/AddressItem';
import { Company, CompanyBasic, CompanyUpdateInput } from '~hooks/useCompany/models';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { imageUrlToBase64 } from '~utils/image/imageToBase64';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';

export const TREAD_UUID = 'ba6c466a-e91c-4cfe-bdd0-f9c41d00ba98';
const isUUID = (id: string) => string().uuid().isValidSync(id);

interface UpdateCompanyParams {
  company: CompanyUpdateInput;
}
interface UploadLogoParams {
  id: string;
  file: File;
  callBack?: (company: Company) => void;
}
interface DeleteLogoParams {
  id: string;
}
interface GetCompanyByIdProps {
  id: string;
  ignoreCompanyNotFound?: boolean;
  signal?: AbortSignal;
}

interface GetCompanyTypeaheadParams {
  limit?: number;
  query?: string;
  cursor?: string;
}

interface GetAllConnectedCompaniesByCompanyIdParams {
  companyId: string;
  managedCompanyId?: string;
  limit?: number;
  callback?: (companies: CompanyBasic[]) => void;
}

interface GetAllConnectedCompaniesByCompanyIdTypeaheadParams extends GetAllConnectedCompaniesByCompanyIdParams {
  query?: string;
  cursor?: string;
}

export const useCompany = () => {
  const { userStore } = useStores();
  const { createDirectUpload } = useDirectUploads();

  const getUserCompany = async (id: string) => {
    const errorMessage = isUUID(id)
      ? t('error_messages.company.failed_to_fetch_user_company')
      : t('error_messages.company.invalid_company_id');
    try {
      const response = await getV1CompaniesId({ path: { id } });

      const company = Company.parse(response.data.data);
      userStore.setUserCompany(company);

      return company;
    } catch (error) {
      connection.handleRequestError(error, errorMessage);
    }
  };

  const getCompanyById = async ({
    id,
    ignoreCompanyNotFound,
    signal,
  }: GetCompanyByIdProps) => {
    const fetchCompany = async () => {
      const response = await getV1CompaniesId({ path: { id }, signal });

      return Company.parse(response.data.data);
    };

    try {
      if (ignoreCompanyNotFound) {
        return await withSentryIgnoreRules(
          SentryIgnoreRule.IGNORE_COMPANY_BY_ID_404,
          fetchCompany,
        );
      }

      return await fetchCompany();
    } catch (error) {
      // Cross-company tickets fetch a company outside the user's scope; 404 expected.
      if (
        ignoreCompanyNotFound &&
        isAxiosError(error) &&
        error.response?.status === 404
      ) {
        return null;
      }

      connection.handleRequestError(
        error,
        t('error_messages.company.failed_to_fetch_company'),
      );
    }
  };

  // Logo URLs are signed and expire, so a PDF that embeds the logo must fetch a fresh
  // URL instead of reusing the one loaded into `userStore.userCompany` at sign-in.
  const getCompanyLogoUrl = useCallback(async (companyId: string) => {
    try {
      const response = await getV1CompaniesCompanyIdLogo({
        path: { 'company-id': companyId },
      });

      return response.data.data.logo_url;
    } catch (error) {
      // Report without rethrowing so the PDF still generates, just without the logo.
      reportRequestError(error, t('error_messages.company.failed_to_fetch_logo'));

      return null;
    }
  }, []);

  const getCompanyLogoBase64 = useCallback(
    async (companyId: string) => {
      const logoUrl = await getCompanyLogoUrl(companyId);
      if (!logoUrl) {
        return null;
      }

      const base64 = await imageUrlToBase64(logoUrl);
      if (!base64) {
        console.warn(`Failed to convert company logo to base64: ${logoUrl}`);
      }

      return base64;
    },
    [getCompanyLogoUrl],
  );

  const updateCompany = async ({ company }: UpdateCompanyParams) => {
    try {
      const response = await patchV1CompaniesId({
        path: { id: company.id },
        body: Company.deparseUpdate(company),
      });
      const updatedCompany = Company.parse(response.data.data);
      userStore.updateUserCompany(updatedCompany);

      return updatedCompany;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.company.failed_to_update'));
    }
  };

  const uploadLogo = async ({ id, file, callBack }: UploadLogoParams) => {
    try {
      const directUpload = await createDirectUpload(file);
      const resp = await postV1CompaniesCompanyIdLogo({
        path: { 'company-id': id },
        body: { logo: directUpload.signed_id },
      });
      const company = Company.parse(resp.data.data);
      userStore.updateUserCompany(company);
      callBack?.(company);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company.failed_to_upload_logo'),
      );
    }
  };

  const deleteLogo = async ({ id }: DeleteLogoParams) => {
    try {
      await deleteV1CompaniesCompanyIdLogo({ path: { 'company-id': id } });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company.failed_to_delete_logo'),
      );
    }

    try {
      const company = await getCompanyById({ id });
      if (company) {
        userStore.updateUserCompany(company);
      }
      return company;
    } catch {
      // getCompanyById handles its own error — swallow the re-throw
      // so the caller still knows the delete succeeded
    }
  };

  const getAllUserAvailableCompanies = async () => {
    const query: GetV1CompaniesData['query'] = { 'page[limit]': MAX_API_PAGE_SIZE };

    try {
      const response = await getV1Companies({ query });
      const companies = response.data.data.map((company) => CompanyBasic.parse(company));
      if (!userStore.userCompanies.length) {
        userStore.setUserCompanies(companies);
      }
      return companies;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company.failed_to_fetch_companies'),
      );
    }
  };

  const getAllUserAvailableCompaniesTypeahead = async (
    options: GetCompanyTypeaheadParams,
  ) => {
    const query: GetV1CompaniesTypeaheadData['query'] = {
      'page[limit]': options?.limit || 20,
      ...(options?.query && { 'search[query]': options.query }),
      ...(options?.cursor && { 'page[after]': options.cursor }),
    };

    try {
      const response = await getV1CompaniesTypeahead({ query });
      const formattedCompanies = response.data.data.map((company) =>
        CompanyBasic.parse(company as Company_Read_Nested),
      );
      const pagination = extractPagination(response);

      return { data: formattedCompanies, pagination };
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.company.failed_to_fetch_companies'),
        );
      } catch (handledError) {
        console.error(handledError);
      }
    }
  };

  const getAllConnectedCompaniesByCompanyId = async (
    options: GetAllConnectedCompaniesByCompanyIdParams,
  ) => {
    const query: GetV1CompaniesCompanyIdConnectedCompaniesData['query'] = {
      'page[limit]': options?.limit || DEFAULT_API_PAGE_SIZE,
    };

    try {
      const response = options.managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdConnectedCompanies(
            {
              path: {
                'company-id': options.companyId,
                'managed-company-id': options.managedCompanyId,
              },
              query,
            },
          )
        : await getV1CompaniesCompanyIdConnectedCompanies({
            path: { 'company-id': options.companyId },
            query,
          });

      const companies = (response.data?.data ?? []).map((company) =>
        CompanyBasic.parse(company as Company_Read_Nested),
      );

      options.callback?.(companies);
      return companies;
    } catch (e) {
      console.error(e);
    }
  };

  const getAllConnectedCompaniesByCompanyIdTypeahead = async (
    options: GetAllConnectedCompaniesByCompanyIdTypeaheadParams,
  ) => {
    const query: GetV1CompaniesCompanyIdConnectedCompaniesTypeaheadData['query'] = {
      'page[limit]': options?.limit || DEFAULT_API_PAGE_SIZE,
      ...(options?.query && { 'search[query]': options.query }),
      ...(options?.cursor && { 'page[after]': options.cursor }),
    };

    try {
      const response = options.managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdConnectedCompaniesTypeahead(
            {
              path: {
                'company-id': options.companyId,
                'managed-company-id': options.managedCompanyId,
              },
              query,
            },
          )
        : await getV1CompaniesCompanyIdConnectedCompaniesTypeahead({
            path: { 'company-id': options.companyId },
            query,
          });

      const formattedCompanies = (response.data?.data ?? []).map((company) =>
        CompanyBasic.parse(company as Company_Read_Nested),
      );
      const pagination = extractPagination(response);

      return { data: formattedCompanies, pagination };
    } catch (e) {
      console.error(e);
    }
  };

  // Memoized at the hook level (not inside the getter) so the getter stays an
  // ordinary function: a useMemo inside it would register on whichever
  // component happens to call it, violating the Rules of Hooks.
  const defaultLon = userStore.userCompany?.defaultLon;
  const defaultLat = userStore.userCompany?.defaultLat;
  const addressLat = userStore.userCompany?.address?.lat;
  const addressLng = userStore.userCompany?.address?.lng;
  const userCompanyDefaultMapCenter = useMemo(() => {
    if (defaultLon && defaultLat) {
      return {
        lng: defaultLon,
        lat: defaultLat,
      };
    }

    // A company that never set a centre falls back to its own street address rather
    // than the global constant, matching what Settings > General Settings shows. The
    // form deliberately does not persist this — see `getCompanyDefaultValues`.
    //
    // Shared with CompanyForm so the two cannot disagree about what counts as
    // geocoded; a finite-only check would send every company with an ungeocoded
    // address to Null Island.
    if (
      addressIsGeocoded(addressLat, addressLng) &&
      addressLng !== undefined &&
      addressLat !== undefined
    ) {
      return { lng: addressLng, lat: addressLat };
    }

    return DEFAULT_MAP_CENTER;
  }, [defaultLon, defaultLat, addressLng, addressLat]);

  const getUserCompanyDefaultMapCenter = () => userCompanyDefaultMapCenter;

  return {
    deleteLogo,
    getAllUserAvailableCompanies,
    getAllUserAvailableCompaniesTypeahead,
    getAllConnectedCompaniesByCompanyId,
    getAllConnectedCompaniesByCompanyIdTypeahead,
    getCompanyById,
    getCompanyLogoBase64,
    getCompanyLogoUrl,
    getUserCompany,
    getUserCompanyDefaultMapCenter,
    updateCompany,
    uploadLogo,
  } as const;
};
